/**
 * Where a picture sits inside the full-screen viewer, as arithmetic
 * (SPEC-012 PR-12.1).
 *
 * Every number that screen computes is here, and this module imports nothing:
 * jsdom reports every layout measurement as zero, so the only way the gesture
 * model gets proven outside a real browser is by being separable from one. It is
 * the same split `cardGesture.js` makes one directory over, for the same reason.
 *
 * A **view** is `{ scale, x, y }`. `scale` is a multiple of the picture's FITTED
 * size — 1 is the whole picture on the whole screen, and there is nothing below
 * it — and `x`/`y` are the picture's offset from the centre of its frame, in CSS
 * pixels, which is the offset a `translate` applies to an element whose
 * transform origin is its own centre.
 *
 * Points passed in are measured from the FRAME's centre, for the same reason:
 * the transform is centre-anchored, so an anchor expressed from the same origin
 * needs no correction term anywhere below.
 */

/** The whole picture, in the middle of the screen. There is no zoom below this. */
export const FIT_SCALE = 1;

/**
 * The ceiling, in multiples of fit.
 *
 * Three, knowing this asks for pixels most files do not have (SPEC-012 D-2). The
 * art build's widest rung is 1600px, and on a 390pt phone at 3× the fitted
 * picture is already about 1170 device pixels — so a ceiling the delivery could
 * serve sharply would be roughly 1.4×, which is not a zoom, it is a nudge. The
 * reader pinching into the F40's engine bay wants to see the plumbing; slightly
 * soft plumbing is what they get, and a gesture that stopped short of it would
 * read as a bug rather than as restraint.
 */
export const MAX_SCALE = 3;

/** Where a double tap lands when there is nothing to fill. See `doubleTapScale`. */
export const STEP_SCALE = 2;

/**
 * How much bigger than the frame a picture has to become before FILLING it is
 * worth doing. Below this a "fill" is a gesture that visibly does nothing.
 */
const FILL_WORTH_IT = 1.05;

export const FITTED_VIEW = { scale: FIT_SCALE, x: 0, y: 0 };

export function clampScale(scale) {
    // NaN — two pointers reported at the same coordinates divide a spread by
    // zero — is not "greater than", so it is caught by this guard rather than by
    // an `isNaN` check that would read as defensive noise.
    return scale > FIT_SCALE ? Math.min(scale, MAX_SCALE) : FIT_SCALE;
}

export function isFitted(view) {
    return view.scale <= FIT_SCALE;
}

/**
 * The scale at which the picture COVERS its frame rather than fitting inside it.
 *
 * This is what a double tap aims for, because it is what iOS Photos does: it
 * does not zoom to a fixed multiple, it fills the screen. On this catalogue the
 * difference is not subtle. The takes are cut-outs at wildly different aspect
 * ratios — a side profile is fitted at about a third of a portrait phone's
 * height and filling it is a 2.5× jump; the F40's headlight is nearly square and
 * filling it is 1.1×. A flat 2× is wrong in both directions at once.
 *
 * Clamped like any other scale, so a picture that would need more than the
 * ceiling stops at the ceiling.
 */
export function fillScale(frame, fitted) {
    if (!(fitted.width > 0) || !(fitted.height > 0)) {
        return FIT_SCALE;
    }

    return clampScale(Math.max(frame.width / fitted.width, frame.height / fitted.height));
}

/**
 * What one double tap asks for.
 *
 * Filling the frame, unless the picture very nearly fills it already — a square
 * take in a square window fits both axes at once, and "cover" would then be a
 * gesture that does nothing at all. A plain step in is the better answer there,
 * because the reader who double-tapped was asking to get closer either way.
 */
export function doubleTapScale(frame, fitted) {
    const fill = fillScale(frame, fitted);

    return fill > FILL_WORTH_IT ? fill : STEP_SCALE;
}

/**
 * How far the picture may travel from centre on each axis before its own edge
 * would come inside the frame.
 *
 * Zero on both axes at fit, because a fitted picture is no larger than its frame
 * — which is what makes "a picture that cannot move must not twitch" a
 * consequence of the clamp rather than a rule anything has to remember.
 *
 * @param {{width: number, height: number}} frame - the slide, one screen
 * @param {{width: number, height: number}} fitted - the picture's laid-out size
 * @param {number} scale
 */
export function panBounds(frame, fitted, scale) {
    return {
        maxX: Math.max(0, (fitted.width * scale - frame.width) / 2),
        maxY: Math.max(0, (fitted.height * scale - frame.height) / 2),
    };
}

/**
 * The view after one step of a gesture that moved its anchor from `from` to `to`
 * and changed the scale to `scale`.
 *
 * The picture-space point under the anchor is invariant across the step, which
 * is what "it zooms around the point between your fingers" means when written
 * down. A drag is the case where the scale does not change, and a zoom about a
 * still point is the case where the anchor does not move — so panning, pinching,
 * double-tapping, wheeling and the arrow keys are all this one function, and
 * there is no second path for any of them to disagree on (SPEC-012 D-4).
 *
 * @param {{scale: number, x: number, y: number}} view - where the picture is now
 * @param {{scale?: number, from: {x: number, y: number}, to: {x: number, y: number}}} step
 */
export function stepView(view, { scale = view.scale, from, to }) {
    const next = clampScale(scale);
    const ratio = next / view.scale;

    return {
        scale: next,
        x: to.x - (from.x - view.x) * ratio,
        y: to.y - (from.y - view.y) * ratio,
    };
}

function clampAxis(offset, limit) {
    // Guarded rather than clamped straight through: `Math.min(Math.max(offset,
    // -0), 0)` answers NEGATIVE zero, which is what every axis of every fitted
    // picture would then carry into its transform and into every test that
    // compares one.
    if (limit <= 0) {
        return 0;
    }

    return Math.min(Math.max(offset, -limit), limit);
}

/** The same view, moved back inside what the picture actually has to give. */
export function clampView(view, frame, fitted) {
    const { maxX, maxY } = panBounds(frame, fitted, view.scale);

    return {
        scale: view.scale,
        x: clampAxis(view.x, maxX),
        y: clampAxis(view.y, maxY),
    };
}

/**
 * The horizontal travel the clamp refused, signed the way the finger was going.
 *
 * This is the carousel's share of the gesture (SPEC-012 D-7): a drag that has run
 * out of picture has not run out of intent, and what is left of it is how far the
 * reader has pulled the next take into view.
 */
export function panOverflowX(view, frame, fitted) {
    return view.x - clampView(view, frame, fitted).x;
}

/** The midpoint between the live pointers — a pinch's anchor, and a drag's. */
export function pinchAnchor(points) {
    const total = points.reduce(
        (sum, point) => ({ x: sum.x + point.x, y: sum.y + point.y }),
        { x: 0, y: 0 },
    );

    return { x: total.x / points.length, y: total.y / points.length };
}

/** How far apart the two fingers are: the numerator and denominator of a pinch. */
export function pinchSpread([first, second]) {
    return Math.hypot(second.x - first.x, second.y - first.y);
}
